import React, { useState, useEffect } from 'react';
import { Plus, Search, Building, Users, FileText, Eye, Upload, Download, X, Trash2, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import { auth, db } from '../../config/firebase';
import { collection, getDocs, doc, writeBatch } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useToast } from '../../components/ui/ToastProvider';
import { H1, H2, H3 } from '../../components/typography';
import Button from '../../components/button';
import * as XLSX from 'xlsx';
import { cleanupMemberRegistry } from '../../api/housing';
import { suggestAssociationSlug, isValidSlug, publicAssociationLabel } from '../../utils/associationSlug';

export default function HyriaAssociations() {
  const navigate = useNavigate();
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAssociation, setEditingAssociation] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importingAssociation, setImportingAssociation] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tempPasswordModal, setTempPasswordModal] = useState({ show: false, email: '' });
  const [error, setError] = useState('');
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupAssociation, setCleanupAssociation] = useState(null);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    organizationNumber: '',
    boardEmail: '',
    numberOfApartments: '',
    slug: '',
    contactPerson: {
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    }
  });
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const toast = useToast();

  // Initiera Firebase Functions
  const functions = getFunctions();

  useEffect(() => {
    fetchAssociations();
  }, []);

  const fetchAssociations = async () => {
    setLoading(true);
    try {
      // Hämta föreningar
      const querySnapshot = await getDocs(collection(db, 'associations'));
      const associationsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Hämta alla lägenheter för statistik
      const housingSnapshot = await getDocs(collection(db, 'housing'));
      const housingData = housingSnapshot.docs.map(doc => doc.data());

      // Hämta alla ansökningar för statistik
      const applicationsSnapshot = await getDocs(collection(db, 'applications'));
      const applicationsData = applicationsSnapshot.docs.map(doc => doc.data());

      // Hämta alla avvikelserapport-beställningar för statistik
      const auditOrdersSnapshot = await getDocs(collection(db, 'auditReportOrders'));
      const auditOrdersData = auditOrdersSnapshot.docs.map(doc => doc.data());

      // Beräkna statistik för varje förening
      const associationsWithStats = associationsData.map(association => {
        // Räkna lägenheter för denna förening
        const totalApartments = housingData.filter(housing => 
          housing.associationId === association.id
        ).length;

        // Räkna ansökningar för denna förening
        const associationApplications = applicationsData.filter(app => 
          app.associationId === association.id
        );
        const totalApplications = associationApplications.length;

        // Räkna aktiva ansökningar (pending, approved, active, etc.)
        const activeApplications = associationApplications.filter(app => 
          app.status && !['REJECTED', 'EXPIRED', 'COMPLETED', 'FINAL_REJECTED'].includes(app.status.toUpperCase())
        ).length;

        // Räkna pending avvikelserapporter
        const pendingAuditOrders = auditOrdersData.filter(o =>
          o.associationId === association.id && o.status === 'pending'
        ).length;

        return {
          ...association,
          stats: {
            ...(association.stats || {}),
            totalApartments,
            totalApplications,
            activeApplications,
            completedApplications: totalApplications - activeApplications,
            pendingAuditOrders
          }
        };
      });

      setAssociations(associationsWithStats);
    } catch (error) {
      toast({ type: 'error', message: 'Kunde inte hämta föreningar' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        ...(field === 'name' && !slugTouched && !editingAssociation ? { slug: suggestAssociationSlug(value) } : {}),
      }));
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) return 'Föreningsnamn är obligatoriskt';
    if (!formData.organizationNumber.trim()) return 'Organisationsnummer är obligatoriskt';
    if (!formData.boardEmail.trim()) return 'Styrelse-email är obligatoriskt';
    if (!formData.boardEmail.includes('@')) return 'Ogiltig email-adress';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      // Validera input
      if (!formData.name.trim() || !formData.organizationNumber.trim() || !formData.boardEmail.trim()) {
        setError('Alla fält är obligatoriska');
        return;
      }

      if (!formData.boardEmail.includes('@')) {
        setError('Ogiltig e-postadress');
        return;
      }

      if (formData.slug && !isValidSlug(formData.slug.trim())) {
        setError('Länken får bara innehålla små bokstäver, siffror och bindestreck');
        return;
      }

      const createAssociation = httpsCallable(functions, 'createAssociation');
      const result = await createAssociation({
        name: formData.name.trim(),
        organizationNumber: formData.organizationNumber.trim(),
        slug: formData.slug.trim() || undefined,
        boardEmail: formData.boardEmail.trim(),
        numberOfApartments: formData.numberOfApartments
          ? parseInt(formData.numberOfApartments, 10)
          : null,
        contactPerson: {
          firstName: formData.contactPerson.firstName.trim(),
          lastName: formData.contactPerson.lastName.trim(),
          email: formData.contactPerson.email.trim(),
          phone: formData.contactPerson.phone.trim()
        }
      });

      if (result.data.success) {
        toast({ type: 'success', message: result.data.message });
        setShowAddModal(false);
        setFormData({
          name: '',
          organizationNumber: '',
          boardEmail: '',
          numberOfApartments: '',
          slug: '',
          contactPerson: { firstName: '', lastName: '', email: '', phone: '' }
        });
        setTempPasswordModal({ show: true, email: formData.boardEmail });
        fetchAssociations(); // Uppdatera föreningslista med statistik
      }
    } catch (err) {
      setError(err.message || 'Ett fel uppstod vid skapande av förening');
    } finally {
      setSaving(false);
    }
  };

  // Ge alla föreningar utan slug en länk på hyria.se (engångskörning)
  const handleBackfillSlugs = async () => {
    setBackfilling(true);
    try {
      const fn = httpsCallable(functions, 'backfillAssociationSlugs');
      const res = await fn({});
      toast({ type: 'success', message: `Länkar skapade för ${res.data.updated} föreningar` });
      fetchAssociations();
    } catch (err) {
      toast({ type: 'error', message: err.message || 'Kunde inte generera länkar' });
    } finally {
      setBackfilling(false);
    }
  };

  // Öppna redigeringsmodal med befintlig data
  const handleEditAssociation = (association) => {
    setEditingAssociation(association);
    setFormData({
      name: association.name || '',
      organizationNumber: association.organizationNumber || '',
      boardEmail: association.boardEmail || '',
      slug: association.slug || '',
      numberOfApartments:
        typeof association.numberOfApartments === 'number'
          ? String(association.numberOfApartments)
          : '',
      contactPerson: {
        firstName: association.contactPerson?.firstName || '',
        lastName: association.contactPerson?.lastName || '',
        email: association.contactPerson?.email || '',
        phone: association.contactPerson?.phone || ''
      }
    });
    setSlugTouched(true);
    setShowEditModal(true);
    setError('');
  };

  // Uppdatera förening
  const handleUpdateAssociation = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      // Validera input (organisationsnummer behöver inte valideras vid uppdatering)
      if (!formData.name.trim() || !formData.boardEmail.trim()) {
        setError('Föreningsnamn och styrelse-email är obligatoriska');
        return;
      }

      if (!formData.boardEmail.includes('@')) {
        setError('Ogiltig e-postadress');
        return;
      }

      if (formData.slug && !isValidSlug(formData.slug.trim())) {
        setError('Länken får bara innehålla små bokstäver, siffror och bindestreck');
        return;
      }

      const updateAssociation = httpsCallable(functions, 'updateAssociation');
      const result = await updateAssociation({
        associationId: editingAssociation.id,
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        boardEmail: formData.boardEmail.trim(),
        numberOfApartments: formData.numberOfApartments === ''
          ? null
          : parseInt(formData.numberOfApartments, 10),
        contactPerson: {
          firstName: formData.contactPerson.firstName.trim(),
          lastName: formData.contactPerson.lastName.trim(),
          email: formData.contactPerson.email.trim(),
          phone: formData.contactPerson.phone.trim()
        }
      });

      if (result.data.success) {
        // Saknade föreningen styrelsekonto skapar servern det vid sparningen.
        const boardAccount = result.data.boardAccount;
        if (boardAccount?.status === 'created' || boardAccount?.status === 'promoted') {
          toast({
            type: 'success',
            message: `Förening uppdaterad. Styrelsekonto klart för ${boardAccount.email}: be dem klicka på "Glömt lösenord" på inloggningssidan.`,
          });
        } else if (boardAccount?.status === 'failed') {
          toast({ type: 'error', message: `Förening uppdaterad, men styrelsekontot kunde inte skapas: ${boardAccount.message}` });
        } else {
          toast({ type: 'success', message: 'Förening uppdaterad framgångsrikt' });
        }
        setShowEditModal(false);
        setEditingAssociation(null);
        setFormData({
          name: '',
          organizationNumber: '',
          boardEmail: '',
          numberOfApartments: '',
          slug: '',
          contactPerson: { firstName: '', lastName: '', email: '', phone: '' }
        });
        fetchAssociations(); // Uppdatera föreningslista med statistik
      }
    } catch (err) {
      setError(err.message || 'Ett fel uppstod vid uppdatering av förening');
    } finally {
      setSaving(false);
    }
  };

  // Öppna import-modal för specifik förening
  const handleImportMembers = (association) => {
    setImportingAssociation(association);
    setShowImportModal(true);
    setError('');
    setUploadedFile(null);
    setPreviewData([]);
  };

  // Ladda ner Excel-mall
  const downloadTemplate = () => {
    const templateData = [
      ['Lägenhetsnummer', 'Adress', 'Ort', 'Postnummer', 'Förnamn', 'Efternamn', 'Juridisk person', 'E-post', 'Telefon'],
      ['1101', 'Storgatan 1', 'Stockholm', '11122', 'Anna', 'Andersson', '', 'anna@example.com', '070-1234567'],
      ['1102', 'Storgatan 1', 'Stockholm', '11122', '', '', 'Andersson AB', 'info@andersson.se', '08-1234567'],
      ['1201', 'Storgatan 1', 'Stockholm', '11122', 'Erik', 'Eriksson', '', 'erik@example.com', '070-9876543']
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Medlemsförteckning');
    XLSX.writeFile(wb, `Medlemsförteckning_Mall_${importingAssociation?.name || 'BRF'}.xlsx`);
  };

  // Hantera fil-upload
  const handleFileUpload = (file) => {
    if (!file) return;
    
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel' // .xls
    ];
    
    if (!validTypes.includes(file.type)) {
      toast({ type: 'error', message: 'Endast Excel (.xlsx) filer är tillåtna' });
      return;
    }
    
    setUploadedFile(file);
    parseFile(file);
  };

  // Parsa Excel-fil
  const parseFile = async (file) => {
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { 
        type: 'array',
        codepage: 65001 // UTF-8
      });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(firstSheet, { 
        header: 1,
        defval: '',
        blankrows: false
      });
      
      if (jsonData.length < 2) {
        toast({ type: 'error', message: 'Filen måste innehålla minst en header-rad och en datarad' });
        return;
      }
      
      // Hitta första raden med data
      let headerRowIndex = 0;
      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (row && row.some(cell => cell && cell.toString().trim().length > 0)) {
          headerRowIndex = i;
          break;
        }
      }
      
      // Rensa data från header-raden
      const cleanedData = jsonData
        .slice(headerRowIndex)
        .filter(row => row && row.some(cell => cell && cell.toString().trim().length > 0))
        .map(row => row.map(cell => cell ? cell.toString().trim() : ''));
      
      if (cleanedData.length < 2) {
        toast({ type: 'error', message: 'Kunde inte hitta header-rad och datarader i filen' });
        return;
      }
      
      setPreviewData(cleanedData);
      toast({ type: 'success', message: `Fil uppladdad! ${cleanedData.length - 1} rader hittades` });
      
    } catch (error) {
      toast({ type: 'error', message: 'Kunde inte läsa filen. Kontrollera att det är en giltig Excel-fil.' });
    }
  };

  // Validera rad-data
  const validateRow = (row, headers) => {
    const errors = [];
    const columnIndexes = {};
    
    // Hitta kolumn-index för obligatoriska fält
    const requiredColumns = {
      apartmentNumber: ['lägenhetsnummer', 'lgh', 'apartment'],
      address: ['adress', 'address'],
      city: ['ort', 'stad', 'city'],
      zipCode: ['postnummer', 'zip', 'postkod']
    };
    
    const optionalColumns = {
      firstName: ['förnamn', 'firstname', 'first name'],
      lastName: ['efternamn', 'lastname', 'last name'],
      legalEntity: ['juridisk person', 'företag', 'company', 'legal entity'],
      email: ['e-post', 'email', 'mail'],
      phone: ['telefon', 'phone', 'tel']
    };
    
    // Hitta kolumn-index
    Object.keys(requiredColumns).forEach(key => {
      columnIndexes[key] = -1;
      const searchTerms = requiredColumns[key];
      for (let i = 0; i < headers.length; i++) {
        const header = headers[i].toLowerCase().trim();
        if (searchTerms.some(term => header.includes(term))) {
          columnIndexes[key] = i;
          break;
        }
      }
    });
    
    Object.keys(optionalColumns).forEach(key => {
      columnIndexes[key] = -1;
      const searchTerms = optionalColumns[key];
      for (let i = 0; i < headers.length; i++) {
        const header = headers[i].toLowerCase().trim();
        if (searchTerms.some(term => header.includes(term))) {
          columnIndexes[key] = i;
          break;
        }
      }
    });
    
    // Validera obligatoriska kolumner
    if (columnIndexes.apartmentNumber === -1) errors.push('Lägenhetsnummer saknas');
    if (columnIndexes.address === -1) errors.push('Adress saknas');
    if (columnIndexes.zipCode === -1) errors.push('Postnummer saknas');
    if (columnIndexes.city === -1) errors.push('Ort saknas');

    // Kontrollera att antingen (förnamn + efternamn) ELLER juridisk person finns
    const hasPersonalName = columnIndexes.firstName !== -1 && columnIndexes.lastName !== -1;
    const hasLegalEntity = columnIndexes.legalEntity !== -1;
    
    if (!hasPersonalName && !hasLegalEntity) {
      errors.push('Minst ett av följande måste finnas: (Förnamn + Efternamn) eller Juridisk person');
    }

    return { errors, columnIndexes };
  };

  // Hantera import
  const handleImport = async () => {
    if (!previewData || previewData.length < 2 || !importingAssociation) {
      toast({ type: 'error', message: 'Ingen data att importera' });
      return;
    }

    setImporting(true);
    
    try {
      const importMembersForAssociation = httpsCallable(functions, 'importMembersForAssociation');
      const result = await importMembersForAssociation({
        associationId: importingAssociation.id,
        memberData: previewData
      });

      if (result.data.success) {
        const { successful, updated, duplicate, failed } = result.data.results;

        let message = '';
        if (successful > 0 || updated > 0) {
          if (successful > 0) {
            message = `🎉 ${successful} nya medlemmar importerade till ${importingAssociation.name}`;
          }
          if (updated > 0) {
            if (message) message += ', ';
            message += `✏️ ${updated} medlemmar uppdaterade`;
          }
          if (duplicate > 0) {
            message += `, ${duplicate} oförändrade`;
          }
          if (failed > 0) {
            message += `, ${failed} fel`;
          }

          if (failed === 0 && duplicate === 0) {
            toast({ type: 'success', message });
            setShowImportModal(false);
            resetImport();
            fetchAssociations();
          } else {
            toast({ type: 'warning', message });
          }
        } else {
          if (duplicate > 0 && failed === 0) {
            toast({
              type: 'warning',
              message: `Alla ${duplicate} rader var oförändrade`
            });
          } else {
            toast({
              type: 'error',
              message: `Import misslyckades: ${failed} fel`
            });
          }
        }
      }
      
    } catch (error) {
      toast({ type: 'error', message: `Import misslyckades: ${error.message}` });
    } finally {
      setImporting(false);
    }
  };

  // Återställ import-state
  const resetImport = () => {
    setUploadedFile(null);
    setPreviewData([]);
    setImportingAssociation(null);
  };

  // Hantera rensa medlemsregister
  const handleCleanupRegistry = async (association) => {
    setCleanupAssociation(association);
    setShowCleanupModal(true);
    setCleaning(true);
    setCleanupPreview(null);

    try {
      // Först hämta förhandsvisning
      const previewResult = await cleanupMemberRegistry(association.id, true);

      if (previewResult.success) {
        setCleanupPreview({
          count: previewResult.statistics?.toDelete || 0,
          apartmentKeys: previewResult.statistics?.apartmentsWithOwners || []
        });
      } else {
        toast({ type: 'error', message: 'Kunde inte hämta förhandsvisning' });
        setShowCleanupModal(false);
      }
    } catch (error) {
      console.error('Error getting cleanup preview:', error);
      toast({ type: 'error', message: 'Fel vid förhandsvisning' });
      setShowCleanupModal(false);
    } finally {
      setCleaning(false);
    }
  };

  // Bekräfta rensning
  const confirmCleanup = async () => {
    if (!cleanupAssociation) return;

    setCleaning(true);

    try {
      const result = await cleanupMemberRegistry(cleanupAssociation.id, false);

      if (result.success) {
        toast({
          type: 'success',
          message: `✅ ${result.statistics?.deleted || 0} lägenheter rensade från medlemsregistret för ${cleanupAssociation.name}`
        });
        setShowCleanupModal(false);
        setCleanupAssociation(null);
        setCleanupPreview(null);
        fetchAssociations(); // Uppdatera statistik
      } else {
        toast({ type: 'error', message: result.error || 'Rensning misslyckades' });
      }
    } catch (error) {
      console.error('Error cleaning registry:', error);
      toast({ type: 'error', message: 'Ett fel uppstod vid rensning' });
    } finally {
      setCleaning(false);
    }
  };

  const filteredAssociations = associations.filter(association => {
    const term = searchTerm.toLowerCase();
    // Vissa föreningar saknar namn/organisationsnummer i datan – använd
    // optional chaining så att sökfiltret inte kraschar på undefined-fält.
    return (
      association.name?.toLowerCase().includes(term) ||
      association.organizationNumber?.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow">
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-48"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <H1>Föreningar</H1>
          <p className="text-[#50535A] mt-1">Hantera föreningar som använder Hyria-systemet</p>
        </div>
        <button
          onClick={() => { setSlugTouched(false); setShowAddModal(true); }}
          className="flex items-center gap-2 px-4 py-2 border border-[#E2EAF0] rounded-lg text-[#50535A] hover:bg-[#F9FCFF] transition"
        >
          <Plus size={16} />
          Lägg till ny förening
        </button>
      </div>
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Sök */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Sök föreningar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
          />
        </div>
      </div>

      {associations.some(a => !a.slug) && (
        <div className="mb-4 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-3">
          <span className="text-sm text-amber-800">
            {associations.filter(a => !a.slug).length} föreningar saknar länk på hyria.se.
          </span>
          <button
            type="button"
            disabled={backfilling}
            onClick={handleBackfillSlugs}
            className="px-3 py-1.5 text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white rounded-lg disabled:opacity-60"
          >
            {backfilling ? 'Genererar…' : 'Generera länkar'}
          </button>
        </div>
      )}

      {/* Föreningslista */}
      <div className="space-y-4">
        {filteredAssociations.map((association) => (
          <div key={association.id} className="bg-white rounded-xl p-6 shadow border border-gray-100">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Building className="w-6 h-6 text-[#62B8E9]" />
                  <button
                    onClick={() => navigate(`/hyria-dashboard/associations/${association.id}`)}
                    className="text-lg font-semibold text-[#62B8E9] hover:text-[#4A9FD1] transition-colors hover:underline"
                  >
                    {association.name}
                  </button>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    association.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {association.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>
                <div className="text-gray-600 mb-2">
                  {association.organizationNumber} | {association.boardEmail}
                </div>
                {/* Två föreningar kan heta likadant — länken skiljer dem åt. */}
                {association.slug && (
                  <div className="text-sm text-gray-500 mb-2 break-all">
                    {publicAssociationLabel(association.slug)}
                  </div>
                )}
                <div className="flex items-center gap-6 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{association.numberOfApartments || association.stats?.totalApartments || 0} lägenheter</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    <span>{association.stats?.totalApplications || 0} ansökningar</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    <span>{association.stats?.activeApplications || 0} aktiva</span>
                  </div>
                  <div className="flex items-center gap-1" title="Medlemmar som aktiverat sig via hyria.se">
                    <span className="w-2 h-2 bg-[#62B8E9] rounded-full"></span>
                    <span>{association.stats?.activatedMembers || 0} aktiverade</span>
                  </div>
                  {!association.slug && (
                    <span className="text-xs text-amber-700">Länk saknas</span>
                  )}
                  {association.stats?.pendingAuditOrders > 0 && (
                    <div className="flex items-center gap-1">
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full">
                        {association.stats.pendingAuditOrders} avvikelserapport
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  className="p-2 text-gray-400 hover:text-blue-600 transition"
                  onClick={() => handleImportMembers(association)}
                  title="Importera medlemmar"
                >
                  <Upload className="w-5 h-5" />
                </button>
                <button
                  className="p-2 text-gray-400 hover:text-red-600 transition"
                  onClick={() => handleCleanupRegistry(association)}
                  title="Rensa medlemsregister"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <button
                  className="p-2 text-gray-400 hover:text-gray-600 transition"
                  onClick={() => handleEditAssociation(association)}
                  title="Redigera förening"
                >
                  <Eye className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredAssociations.length === 0 && !loading && (
        <div className="text-center py-12">
          <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <H3 className="text-gray-500 mb-2">Inga föreningar hittades</H3>
          <p className="text-gray-400">Skapa din första förening för att komma igång</p>
        </div>
      )}

      {/* Lägg till förening Modal */}
      <Dialog open={showAddModal} onClose={() => {
        setShowAddModal(false);
        setError('');
      }} className="fixed z-50 inset-0 flex items-center justify-center">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="relative bg-white rounded-2xl shadow-lg p-6 w-full max-w-md mx-auto">
          <H2 className="mb-6">Lägg till ny förening</H2>
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Föreningsnamn *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                placeholder="Brf Exempelhuset"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Länk på hyria.se
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 whitespace-nowrap">hyria.se/brf/</span>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => { setSlugTouched(true); handleInputChange('slug', e.target.value.toLowerCase()); }}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                  placeholder="solhojden"
                />
              </div>
              {editingAssociation && editingAssociation.slug && formData.slug !== editingAssociation.slug && (
                <p className="text-xs text-amber-700 mt-1">
                  Byter du länk slutar den gamla länken fungera för alla som redan fått den.
                </p>
              )}
              {!editingAssociation && (
                <p className="text-xs text-gray-500 mt-1">Föreslås från namnet. Lämnas tomt → genereras automatiskt.</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organisationsnummer *
              </label>
              <input
                type="text"
                value={formData.organizationNumber}
                onChange={(e) => handleInputChange('organizationNumber', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                placeholder="123456-7890"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Styrelse-email *
              </label>
              <input
                type="email"
                value={formData.boardEmail}
                onChange={(e) => handleInputChange('boardEmail', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                placeholder="styrelse@brfexempelhuset.se"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Antal lägenheter <span className="text-gray-400 font-normal">(valfritt)</span>
              </label>
              <input
                type="number"
                min="0"
                value={formData.numberOfApartments}
                onChange={(e) => handleInputChange('numberOfApartments', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                placeholder="T.ex. 168"
              />
              <p className="text-xs text-gray-500 mt-1">
                Lämnas tomt → räknas automatiskt från medlemsregistret.
              </p>
            </div>

            <div className="border-t pt-4">
              <H3 className="text-sm font-medium text-gray-700 mb-3">Kontaktperson (valfritt)</H3>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Förnamn</label>
                  <input
                    type="text"
                    value={formData.contactPerson.firstName}
                    onChange={(e) => handleInputChange('contactPerson.firstName', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="Anna"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Efternamn</label>
                  <input
                    type="text"
                    value={formData.contactPerson.lastName}
                    onChange={(e) => handleInputChange('contactPerson.lastName', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="Andersson"
                  />
                </div>
              </div>

              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.contactPerson.email}
                  onChange={(e) => handleInputChange('contactPerson.email', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                  placeholder="anna@brfexempelhuset.se"
                />
              </div>

              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Telefon</label>
                <input
                  type="tel"
                  value={formData.contactPerson.phone}
                  onChange={(e) => handleInputChange('contactPerson.phone', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                  placeholder="070-123 45 67"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setError('');
                }}
                className="flex-1 border border-gray-200 rounded-lg py-3 font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                Avbryt
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#62B8E9] text-white rounded-lg py-3 font-medium hover:bg-[#4A9FD1] transition disabled:opacity-50"
              >
                {saving ? 'Skapar...' : 'Skapa förening'}
              </button>
            </div>
          </form>
        </div>
      </Dialog>

      {/* Redigera förening Modal */}
      <Dialog open={showEditModal} onClose={() => {
        setShowEditModal(false);
        setEditingAssociation(null);
        setError('');
      }} className="fixed z-50 inset-0 flex items-center justify-center">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="relative bg-white rounded-2xl shadow-lg p-6 w-full max-w-md mx-auto">
          <H2 className="mb-6">Redigera förening</H2>
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}
          
          <form onSubmit={handleUpdateAssociation} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Föreningsnamn *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                placeholder="Brf Exempelhuset"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Länk på hyria.se
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 whitespace-nowrap">hyria.se/brf/</span>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => { setSlugTouched(true); handleInputChange('slug', e.target.value.toLowerCase()); }}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                  placeholder="solhojden"
                />
              </div>
              {editingAssociation && editingAssociation.slug && formData.slug !== editingAssociation.slug && (
                <p className="text-xs text-amber-700 mt-1">
                  Byter du länk slutar den gamla länken fungera för alla som redan fått den.
                </p>
              )}
              {!editingAssociation && (
                <p className="text-xs text-gray-500 mt-1">Föreslås från namnet. Lämnas tomt → genereras automatiskt.</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organisationsnummer
              </label>
              <input
                type="text"
                value={formData.organizationNumber}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-500 cursor-not-allowed"
                placeholder="123456-7890"
                disabled
                title="Organisationsnummer kan inte ändras"
              />
              <p className="text-xs text-gray-500 mt-1">Organisationsnummer kan inte ändras</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Styrelse-email *
              </label>
              <input
                type="email"
                value={formData.boardEmail}
                onChange={(e) => handleInputChange('boardEmail', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                placeholder="styrelse@brfexempelhuset.se"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Antal lägenheter <span className="text-gray-400 font-normal">(valfritt)</span>
              </label>
              <input
                type="number"
                min="0"
                value={formData.numberOfApartments}
                onChange={(e) => handleInputChange('numberOfApartments', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                placeholder="T.ex. 168"
              />
              <p className="text-xs text-gray-500 mt-1">
                Lämnas tomt → räknas automatiskt från medlemsregistret.
              </p>
            </div>

            <div className="border-t pt-4">
              <H3 className="text-sm font-medium text-gray-700 mb-3">Kontaktperson</H3>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Förnamn</label>
                  <input
                    type="text"
                    value={formData.contactPerson.firstName}
                    onChange={(e) => handleInputChange('contactPerson.firstName', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="Anna"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Efternamn</label>
                  <input
                    type="text"
                    value={formData.contactPerson.lastName}
                    onChange={(e) => handleInputChange('contactPerson.lastName', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    placeholder="Andersson"
                  />
                </div>
              </div>

              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.contactPerson.email}
                  onChange={(e) => handleInputChange('contactPerson.email', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                  placeholder="anna@brfexempelhuset.se"
                />
              </div>

              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Telefon</label>
                <input
                  type="tel"
                  value={formData.contactPerson.phone}
                  onChange={(e) => handleInputChange('contactPerson.phone', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                  placeholder="070-123 45 67"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingAssociation(null);
                  setError('');
                }}
                className="flex-1 border border-gray-200 rounded-lg py-3 font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                Avbryt
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#62B8E9] text-white rounded-lg py-3 font-medium hover:bg-[#4A9FD1] transition disabled:opacity-50"
              >
                {saving ? 'Uppdaterar...' : 'Uppdatera förening'}
              </button>
            </div>
          </form>
        </div>
      </Dialog>

      {/* Import medlemmar Modal */}
      <Dialog open={showImportModal} onClose={() => {
        setShowImportModal(false);
        resetImport();
      }} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <Dialog.Title className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
                <Upload className="w-6 h-6 text-[#62B8E9]" />
                Importera medlemmar till {importingAssociation?.name}
              </Dialog.Title>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  resetImport();
                }}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Step 1: Download Template */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Download className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-blue-900">Steg 1: Ladda ner mall</span>
                </div>
                <button
                  onClick={downloadTemplate}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors flex items-center gap-1"
                >
                  <Download className="w-3 h-3" />
                  Ladda ner Excel-mall
                </button>
              </div>
            </div>

            {/* Step 2: Upload File */}
            <div className="mb-6 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#62B8E9] transition-colors">
              <div className="text-center">
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <div className="font-medium text-gray-700 mb-1">Steg 2: Ladda upp Excel-fil</div>
                <div className="text-sm text-gray-500 mb-4">Dra och släpp eller klicka för att välja fil</div>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="bg-[#62B8E9] hover:bg-[#5ba3d4] text-white px-4 py-2 rounded text-sm font-medium transition-colors cursor-pointer inline-flex items-center gap-1"
                >
                  <Upload className="w-3 h-3" />
                  Välj Excel-fil
                </label>
                {uploadedFile && (
                  <div className="mt-2 text-sm text-green-600">
                    ✓ {uploadedFile.name} uppladdad
                  </div>
                )}
              </div>
            </div>

            {/* Step 3: Preview Data */}
            {previewData.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4 text-green-600" />
                  <span className="font-medium text-green-900">Steg 3: Förhandsgranska data</span>
                  <span className="text-sm text-gray-500">({previewData.length - 1} rader)</span>
                </div>
                
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-64">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          {previewData[0]?.map((header, i) => (
                            <th key={i} className="px-2 py-2 text-left font-medium text-gray-700 border-b">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.slice(1, 6).map((row, i) => (
                          <tr key={i} className="border-b border-gray-100">
                            {row.map((cell, j) => (
                              <td key={j} className="px-2 py-2 text-gray-600">
                                {cell || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {previewData.length > 6 && (
                    <div className="bg-gray-50 px-4 py-2 text-xs text-gray-500 text-center">
                      ... och {previewData.length - 6} rader till
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Import Button */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowImportModal(false);
                  resetImport();
                }}
                className="px-4 py-2 border border-gray-200 rounded text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleImport}
                disabled={!previewData.length || importing}
                className="px-4 py-2 bg-[#62B8E9] text-white rounded hover:bg-[#5ba3d4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {importing ? (
                  <>
                    <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                    Importerar...
                  </>
                ) : (
                  <>
                    <Upload className="w-3 h-3" />
                    Importera medlemmar
                  </>
                )}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Temporärt lösenord Modal */}
      <Dialog open={tempPasswordModal.show} onClose={() => setTempPasswordModal({ show: false, password: '', associationName: '', email: '' })} className="fixed z-50 inset-0 flex items-center justify-center">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="relative bg-white rounded-2xl shadow-lg p-6 w-full max-w-md mx-auto">
          <H2 className="mb-4 text-center">BRF Admin Skapad</H2>
          
          <div className="text-center mb-6">
            <p className="text-gray-600 mb-4">
              Föreningen <strong>{tempPasswordModal.associationName}</strong> har skapats framgångsrikt!
            </p>
            <p className="text-gray-600 mb-4">
              Ett BRF Admin konto har skapats med följande email:
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="text-sm text-gray-500 mb-2">Email:</div>
              <div className="font-mono text-lg font-semibold text-gray-800 mb-3">
                {tempPasswordModal.email}
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800 font-medium mb-2">Instruktioner för BRF Admin:</p>
              <ol className="text-sm text-blue-700 text-left space-y-1">
                <li>1. Gå till inloggningssidan</li>
                <li>2. Använd email: <strong>{tempPasswordModal.email}</strong></li>
                <li>3. Klicka "Glömt lösenord" för att sätta nytt lösenord</li>
                <li>4. Logga in med det nya lösenordet</li>
              </ol>
            </div>
            
            <p className="text-sm text-gray-500">
              Skicka email-adressen till BRF Admin så de kan sätta upp sitt lösenord.
            </p>
          </div>

          <div className="flex justify-center">
            <button
              onClick={() => setTempPasswordModal({ show: false, password: '', associationName: '', email: '' })}
              className="bg-[#62B8E9] text-white rounded-lg px-6 py-2 font-medium hover:bg-[#4A9FD1] transition"
            >
              Stäng
            </button>
          </div>
        </div>
      </Dialog>

      {/* Cleanup Member Registry Modal */}
      <Dialog open={showCleanupModal} onClose={() => {
        if (!cleaning) {
          setShowCleanupModal(false);
          setCleanupAssociation(null);
          setCleanupPreview(null);
        }
      }} className="fixed z-50 inset-0 flex items-center justify-center">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="relative bg-white rounded-2xl shadow-lg p-6 w-full max-w-md mx-auto">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <H2 className="text-xl font-semibold">Rensa medlemsregister</H2>
              <p className="text-gray-600 mt-1">
                {cleanupAssociation?.name}
              </p>
            </div>
          </div>

          {cleaning && !cleanupPreview && (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Analyserar medlemsregister...</p>
            </div>
          )}

          {cleanupPreview && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm font-medium text-amber-900 mb-2">
                  ⚠️ Varning: Denna åtgärd kommer att ta bort:
                </p>
                <p className="text-2xl font-bold text-amber-900">
                  {cleanupPreview.count} lägenheter
                </p>
                <p className="text-sm text-amber-700 mt-2">
                  Endast lägenheter utan ägare som laddats upp från medlemsregistret kommer att tas bort.
                </p>
              </div>

              {cleanupPreview.count > 0 && cleanupPreview.apartmentKeys.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto">
                  <p className="text-xs font-medium text-gray-700 mb-2">Lägenheter som kommer att tas bort:</p>
                  <div className="space-y-1">
                    {cleanupPreview.apartmentKeys.slice(0, 10).map((key, index) => (
                      <div key={index} className="text-xs text-gray-600 font-mono">
                        {key}
                      </div>
                    ))}
                    {cleanupPreview.apartmentKeys.length > 10 && (
                      <p className="text-xs text-gray-500 italic">
                        ...och {cleanupPreview.apartmentKeys.length - 10} till
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-3 justify-end pt-4">
                <button
                  onClick={() => {
                    setShowCleanupModal(false);
                    setCleanupAssociation(null);
                    setCleanupPreview(null);
                  }}
                  disabled={cleaning}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
                >
                  Avbryt
                </button>
                <button
                  onClick={confirmCleanup}
                  disabled={cleaning || cleanupPreview.count === 0}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {cleaning ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Rensar...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Rensa {cleanupPreview.count} lägenheter
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {cleanupPreview && cleanupPreview.count === 0 && (
            <div className="text-center py-8">
              <div className="p-3 bg-green-100 rounded-full w-fit mx-auto mb-3">
                <AlertCircle className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-gray-700 font-medium">Inget att rensa</p>
              <p className="text-sm text-gray-500 mt-2">
                Det finns inga lägenheter från medlemsregistret att ta bort.
              </p>
              <button
                onClick={() => {
                  setShowCleanupModal(false);
                  setCleanupAssociation(null);
                  setCleanupPreview(null);
                }}
                className="mt-6 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
              >
                Stäng
              </button>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}
